import { OverlayTrigger, Stack, Table, Tooltip, TooltipProps } from 'react-bootstrap';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import Common from '../../stores/Common';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { DownloadIconButton } from '../shared/download-button/DownloadIconButton';
import { IDocumentModel } from '../../interfaces/ICase';
import { showErrorToast } from '../../shared/ToastUtils';
import { useDocumentDownload } from '../../shared/react-query-hooks/useDocumentDownload';
import { useState } from 'react';
import { useZoneContext } from '../../contexts/useZoneContext';

interface IDocumentVersionsListProps {
    document: IDocumentModel;
    entityGuid: string;
    entityTypeId: number;
    noteTypeId?: number;
    strategyTypeId?: number;
}

export function DocumentVersionsList(props: IDocumentVersionsListProps) {
    const { document: doc } = props;

    const zoneContext = useZoneContext();

    const { downloadAsync } = useDocumentDownload({
        endpoint: '/' + ApiRoutes.EntityDocumentDownload,
        onError: (err) => showErrorToast(err.message),
    });

    const [versionsDownloading, setVersionsDownloading] = useState<string[]>([]);
    const [versionsWithErrors, setVersionsWithErrors] = useState<string[]>([]);

    const handleVersionDownload = async (fileVersionGuid: string) => {
        if (!doc.guid) return;

        const handleDownloadSuccess = () => {
            setVersionsWithErrors((prev) => prev.filter((docGuid) => docGuid !== fileVersionGuid));
        };

        const handleDownloadError = () => {
            setVersionsWithErrors((prev) => [...prev, fileVersionGuid]);
        };

        setVersionsDownloading((prev) => [...prev, fileVersionGuid]);

        await downloadAsync({
            entityGuid: props.entityGuid,
            entityTypeId: props.entityTypeId,
            documentVersionGuid: fileVersionGuid,
            noteTypeId: props.noteTypeId,
            strategyTypeId: props.strategyTypeId,
            tenantGuid: zoneContext.zoneGuid,
        })
            .then(handleDownloadSuccess)
            .catch(handleDownloadError)
            .finally(() => {
                setVersionsDownloading((prev) =>
                    prev.filter((docGuid) => docGuid !== fileVersionGuid)
                );
            });
    };

    const sortedDocumentVersions = doc.documentVersions
        ? [...doc.documentVersions].sort((a, b) => {
              const dateA = new Date(a.createdDate ?? '');
              const dateB = new Date(b.createdDate ?? '');
              return dateB.getTime() - dateA.getTime();
          })
        : [];

    return (
        <div>
            <div className="mb-2">
                <strong>File name:</strong> {doc.fileName}
            </div>
            <Table>
                <thead>
                    <tr>
                        <th>Version</th>
                        <th>Created Date</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedDocumentVersions?.map((version, idx) => {
                        const renderTooltip = (props: TooltipProps) => (
                            <Tooltip {...props}>{version.comment}</Tooltip>
                        );

                        const handleDownloadClick = () => {
                            handleVersionDownload?.(version.guid);
                        };

                        return (
                            <>
                                <tr key={version.guid} style={{ verticalAlign: 'middle' }}>
                                    <td>
                                        <Stack
                                            direction="horizontal"
                                            gap={1}
                                            style={{ alignItems: 'center' }}
                                        >
                                            <div style={{ flexBasis: 18 }}>
                                                {version.comment ? (
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        delay={{ show: 250, hide: 400 }}
                                                        overlay={renderTooltip}
                                                    >
                                                        <i className="fal fa-comment" />
                                                    </OverlayTrigger>
                                                ) : (
                                                    <span></span>
                                                )}
                                            </div>
                                            <span>{sortedDocumentVersions.length - idx}</span>
                                        </Stack>
                                    </td>
                                    <td>{Common.dateTimeFormat(version.createdDate)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <DownloadIconButton
                                            isDownloading={
                                                versionsDownloading?.includes(version.guid) ?? false
                                            }
                                            isError={
                                                versionsWithErrors?.includes(version.guid) ?? false
                                            }
                                            onClick={handleDownloadClick}
                                            document={props.document}
                                        />
                                    </td>
                                </tr>
                            </>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}
